import isEqual from "lodash/isEqual";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MedicalQuestion } from "../../../data-model/medicalQuestion";
import {
  AnswerMap,
  buildAnsweredQuestions,
  collectDescendantIds,
  initializeAnswers,
} from "../utils/questionnaireUtils";

/**
 * Manages the answer state for a questionnaire tree.
 *
 * Initialises answers from pre-filled data, handles single-select,
 * multi-select, and free-text changes, and clears descendant answers
 * when a parent selection changes.
 */
export const useQuestionnaireAnswers = (questions: MedicalQuestion[]) => {
  const initialAnswersRef = useRef<AnswerMap | null>(null);
  const [answers, setAnswers] = useState<AnswerMap>(() => {
    const initial = initializeAnswers(questions);
    initialAnswersRef.current = initial;
    return initial;
  });

  useEffect(() => {
    if (questions.length === 0) return;
    const fresh = initializeAnswers(questions);
    if (isEqual(fresh, initialAnswersRef.current)) return;
    initialAnswersRef.current = fresh;
    setAnswers(fresh);
  }, [questions]);

  const handleRadioChange = useCallback(
    (question: MedicalQuestion, value: string) => {
      setAnswers((prev) => {
        const next = { ...prev };
        const prevValue = prev[question.id];
        if (
          typeof prevValue === "string" &&
          prevValue !== value &&
          question.children[prevValue]
        ) {
          collectDescendantIds(question.children[prevValue]).forEach(
            (id) => delete next[id],
          );
        }
        next[question.id] = value;
        return next;
      });
    },
    [],
  );

  const handleCheckChange = useCallback(
    (question: MedicalQuestion, value: string, checked: boolean) => {
      setAnswers((prev) => {
        const next = { ...prev };
        const current = (prev[question.id] as string[]) ?? [];
        if (checked) {
          next[question.id] = [...current, value];
        } else {
          next[question.id] = current.filter((v) => v !== value);
          if (question.children[value]) {
            collectDescendantIds(question.children[value]).forEach(
              (id) => delete next[id],
            );
          }
        }
        return next;
      });
    },
    [],
  );

  const handleTextChange = useCallback((questionId: string, value: string) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  }, []);

  const isDirty = useMemo(
    () => !isEqual(answers, initialAnswersRef.current),
    [answers],
  );

  const answeredQuestions = useMemo(
    () => buildAnsweredQuestions(questions, answers),
    [questions, answers],
  );

  return {
    answers,
    answeredQuestions,
    isDirty,
    handleRadioChange,
    handleCheckChange,
    handleTextChange,
  };
};
